import Text from '../../../components/Text';
import React from 'react';
import loader from '../../../assets/images/datahub-loader.svg';
import { IStateWiseLicenseDetails } from '../../companies/types';

type StateCommodityLicenseData = {
    state: string;
    commodity: string;
    abcActive: number;
    abcInactive: number;
    abcUnknown: number;
    abcTotal: number;
    supplierActive: number;
    supplierInactive: number;
    supplierUnknown: number;
    supplierTotal: number;
    totalLicensesActive: number;
    totalLicensesInactive: number;
    totalLicensesUnknown: number;
    totalLicensesTotal: number;
};

type StateSummaryDataProps = {
    data?: IStateWiseLicenseDetails[];
    loading?: boolean;
};

// Format number: show "-" for 0, otherwise show the number
const formatNumber = (value: number): string | number => {
    return value === 0 ? '-' : value;
};

// Transform API data to component format
const transformApiData = (apiData: IStateWiseLicenseDetails[]): StateCommodityLicenseData[] => {
    if (!apiData || apiData.length === 0) {
        return [];
    }

    return apiData.map(item => ({
        state: item.state || '',
        commodity: item.commodity || 'Unknown',
        abcActive: item.abcActive || 0,
        abcInactive: item.abcInactive || 0,
        abcUnknown: item.abcUnknown || 0,
        abcTotal: item.abcTotal || 0,
        supplierActive: item.supplierActive || 0,
        supplierInactive: item.supplierInactive || 0,
        supplierUnknown: item.supplierUnknown || 0,
        supplierTotal: item.supplierTotal || 0,
        totalLicensesActive: item.totalLicensesActive || 0,
        totalLicensesInactive: item.totalLicensesInactive || 0,
        totalLicensesUnknown: item.totalLicensesUnknown || 0,
        totalLicensesTotal: item.totalLicensesTotal || 0,
    }));
};

export const StateSummaryDataTable = ({
    data = [],
    loading,
}: StateSummaryDataProps) => {
    const transformedData = transformApiData(data);

    // Group data by state to handle rowspan
    const groupedData = transformedData.reduce((acc, item) => {
        if (!acc[item.state]) {
            acc[item.state] = [];
        }
        acc[item.state].push(item);
        return acc;
    }, {} as Record<string, StateCommodityLicenseData[]>);

    const states = Object.keys(groupedData).sort();

    // Calculate totals
    const totals = transformedData.reduce((acc, item) => {
        acc.abcActive += item.abcActive;
        acc.abcInactive += item.abcInactive;
        acc.abcUnknown += item.abcUnknown;
        acc.abcTotal += item.abcTotal;
        acc.supplierActive += item.supplierActive;
        acc.supplierInactive += item.supplierInactive;
        acc.supplierUnknown += item.supplierUnknown;
        acc.supplierTotal += item.supplierTotal;
        acc.totalLicensesActive += item.totalLicensesActive;
        acc.totalLicensesInactive += item.totalLicensesInactive;
        acc.totalLicensesUnknown += item.totalLicensesUnknown;
        acc.totalLicensesTotal += item.totalLicensesTotal;
        return acc;
    }, {
        abcActive: 0,
        abcInactive: 0,
        abcUnknown: 0,
        abcTotal: 0,
        supplierActive: 0,
        supplierInactive: 0,
        supplierUnknown: 0,
        supplierTotal: 0,
        totalLicensesActive: 0,
        totalLicensesInactive: 0,
        totalLicensesUnknown: 0,
        totalLicensesTotal: 0,
    });

    return (
        <>
            <style>{`
                .state-summary-table tbody tr:hover {
                    background-color: rgba(0, 0, 0, 0.03) !important;
                }
                .state-summary-table thead th {
                    padding: 0.5rem !important;
                    vertical-align: middle !important;
                }
                .state-summary-table thead th.text-center {
                    text-align: center !important;
                }
                .state-summary-table .border-thick-left {
                    border-left: 2px solid #dee2e6 !important;
                }
                .state-summary-table .border-thick-right {
                    border-right: 2px solid #dee2e6 !important;
                }
                .state-summary-table .border-thick-top {
                    border-top: 2px solid #dee2e6 !important;
                }
                .state-summary-table .border-thick-bottom {
                    border-bottom: 2px solid #dee2e6 !important;
                }
                .state-summary-table .border-thin-right {
                    border-right: 1px solid #dee2e6 !important;
                }
            `}</style>
            <div className="table-responsive react-table-sticky-header fs-11">
                <table className="table table-centered react-table state-summary-table">
                    <thead className="table-light">
                        <tr className="border-thick-top">
                            <th rowSpan={2} className="text-center border-thick-left border-thick-right border-thick-top">State</th>
                            <th rowSpan={2} className="text-center border-thick-left border-thick-right border-thick-top">Commodity</th>
                            <th colSpan={4} className="text-center border-thick-left border-thick-right border-thick-top">ABC</th>
                            <th colSpan={4} className="text-center border-thick-left border-thick-right border-thick-top">Supplier</th>
                            <th colSpan={4} className="text-center border-thick-left border-thick-right border-thick-top">Total Licenses</th>
                        </tr>
                        <tr>
                            <th className="text-center border-thick-left border-thin-right">Active</th>
                            <th className="text-center border-thin-right">Inactive</th>
                            <th className="text-center border-thin-right">Unknown</th>
                            <th className="text-center border-thick-right">Total</th>
                            <th className="text-center border-thick-left border-thin-right">Active</th>
                            <th className="text-center border-thin-right">Inactive</th>
                            <th className="text-center border-thin-right">Unknown</th>
                            <th className="text-center border-thick-right">Total</th>
                            <th className="text-center border-thick-left border-thin-right">Active</th>
                            <th className="text-center border-thin-right">Inactive</th>
                            <th className="text-center border-thin-right">Unknown</th>
                            <th className="text-center border-thick-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr style={{ height: '150px' }}>
                                <td colSpan={14} className="text-center py-1">
                                    <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
                                        <img src={loader} height={'50px'} alt="Loading..." />
                                    </div>
                                </td>
                            </tr>
                        ) : states.length > 0 ? (
                            states.map((state, stateIndex) => {
                                const stateRows = groupedData[state];
                                const isLastState = stateIndex === states.length - 1;
                                return stateRows.map((row, rowIndex) => {
                                    const isLastRowInState = rowIndex === stateRows.length - 1;
                                    const borderBottomClass = isLastRowInState && !isLastState ? 'border-thick-bottom' : '';
                                    return (
                                    <tr key={`${state}-${row.commodity}`} className={borderBottomClass}>
                                        {rowIndex === 0 && (
                                            <td rowSpan={stateRows.length} className="text-center border-thick-left border-thick-right" style={{ verticalAlign: 'middle' }}>
                                                <Text variant="link" url={`/companies?StateCode=${row.state}`}>
                                                    {row.state}
                                                </Text>
                                            </td>
                                        )}
                                        <td className="text-center border-thick-left border-thick-right">{row.commodity}</td>
                                        <td className="text-center border-thick-left border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=ABC&Commodity=${row.commodity}&LicenseStatus=Active`}>
                                                {formatNumber(row.abcActive)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=ABC&Commodity=${row.commodity}&LicenseStatus=Inactive`}>
                                                {formatNumber(row.abcInactive)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=ABC&Commodity=${row.commodity}&LicenseStatus=Unknown`}>
                                                {formatNumber(row.abcUnknown)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thick-right" style={{ fontWeight: 'bold' }}>
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=ABC&Commodity=${row.commodity}`}>
                                                {formatNumber(row.abcTotal)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thick-left border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=Supplier&Commodity=${row.commodity}&LicenseStatus=Active`}>
                                                {formatNumber(row.supplierActive)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=Supplier&Commodity=${row.commodity}&LicenseStatus=Inactive`}>
                                                {formatNumber(row.supplierInactive)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=Supplier&Commodity=${row.commodity}&LicenseStatus=Unknown`}>
                                                {formatNumber(row.supplierUnknown)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thick-right" style={{ fontWeight: 'bold' }}>
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&LicenseType=Supplier&Commodity=${row.commodity}`}>
                                                {formatNumber(row.supplierTotal)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thick-left border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&Commodity=${row.commodity}&LicenseStatus=Active`}>
                                                {formatNumber(row.totalLicensesActive)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&Commodity=${row.commodity}&LicenseStatus=Inactive`}>
                                                {formatNumber(row.totalLicensesInactive)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thin-right">
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&Commodity=${row.commodity}&LicenseStatus=Unknown`}>
                                                {formatNumber(row.totalLicensesUnknown)}
                                            </Text>
                                        </td>
                                        <td className="text-center border-thick-right" style={{ fontWeight: 'bold' }}>
                                            <Text variant="link" url={`/companies?StateCode=${row.state}&Commodity=${row.commodity}`}>
                                                {formatNumber(row.totalLicensesTotal)}
                                            </Text>
                                        </td>
                                    </tr>
                                );
                                });
                            })
                        ) : (
                            <tr>
                                <td colSpan={14} className="text-center">
                                    No data found
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="border-thick-top border-thick-bottom" style={{ fontWeight: 'bold' }}>
                            <td colSpan={2} className="text-center border-thick-left border-thick-right">
                                Total
                            </td>
                            <td className="text-center border-thick-left border-thin-right">
                                {formatNumber(totals.abcActive)}
                            </td>
                            <td className="text-center border-thin-right">
                                {formatNumber(totals.abcInactive)}
                            </td>
                            <td className="text-center border-thin-right">
                                {formatNumber(totals.abcUnknown)}
                            </td>
                            <td className="text-center border-thick-right">
                                {formatNumber(totals.abcTotal)}
                            </td>
                            <td className="text-center border-thick-left border-thin-right">
                                {formatNumber(totals.supplierActive)}
                            </td>
                            <td className="text-center border-thin-right">
                                {formatNumber(totals.supplierInactive)}
                            </td>
                            <td className="text-center border-thin-right">
                                {formatNumber(totals.supplierUnknown)}
                            </td>
                            <td className="text-center border-thick-right">
                                {formatNumber(totals.supplierTotal)}
                            </td>
                            <td className="text-center border-thick-left border-thin-right">
                                {formatNumber(totals.totalLicensesActive)}
                            </td>
                            <td className="text-center border-thin-right">
                                {formatNumber(totals.totalLicensesInactive)}
                            </td>
                            <td className="text-center border-thin-right">
                                {formatNumber(totals.totalLicensesUnknown)}
                            </td>
                            <td className="text-center border-thick-right">
                                {formatNumber(totals.totalLicensesTotal)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </>
    );
}